import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { Select, react } from "uikit";
import { useTranslation } from "react-i18next";

import PaymentSystem, {
	PaymentCountryIso,
} from "../../../../../../../../services/PaymentSystem";
import useObjectEditor from "../../../../../../../../hooks/useObjectEditor";
import translationPath from "../../../../constants/translationPath";

import InternalController from "./Controller";

const MonobankPaymentsCountryIsoBase = react.withController<
	MonobankPaymentsCountryIso.PropsBase,
	MonobankPaymentsCountryIso.Controller
>(({ value, onChange, controller }) => {
	const [error, setError] = useState<boolean>(false);

	const { t } = useTranslation();

	const valueEditor = useObjectEditor(value, onChange);

	const monobankCountryIso = valueEditor.useGetter("monobankCountryIso");
	const monobankCountryIsoSet = valueEditor.useSetter("monobankCountryIso");

	const selectOptionsWrap = useCallback(
		(item: { id: PaymentCountryIso; label: PaymentCountryIso }) => ({
			key: item.id,
			value: item.id,
			label: item.label,
		}),
		[],
	);

	const options = useMemo(
		() =>
			[PaymentCountryIso.UKR].map((label) =>
				selectOptionsWrap({
					id: label,
					label,
				}),
			),
		[selectOptionsWrap],
	);

	const updateSelectedValues = useCallback(
		(value) => {
			monobankCountryIsoSet(value);
			setError(false);
		},
		[monobankCountryIsoSet],
	);

	const textError = useMemo(() => {
		if (!error) return "";
		return t([
			`${translationPath}.errors.countryIso`,
			"Country not selected",
		]);
	}, [t, error]);

	const placeholder = useMemo(
		() =>
			t([
				`${translationPath}.placeholders.countryIso`,
				"Choose a country",
			]),
		[t],
	);

	controller.setContext({ value, setError });
	return (
		<Select
			style={{ width: "200px" }}
			placeholder={placeholder}
			error={textError}
			value={monobankCountryIso}
			options={options}
			onChange={updateSelectedValues}
		/>
	);
}, InternalController);
const MonobankPaymentsCountryIso = memo(MonobankPaymentsCountryIsoBase);

declare namespace MonobankPaymentsCountryIso {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default MonobankPaymentsCountryIso;
